import { useEffect, useId, type ReactNode } from 'react';
import { Icon } from '@/shared/ui/Icon';

interface ModalProps {
  open: boolean;
  title: string;
  /** O texto de apoio abaixo do título. */
  note?: ReactNode;
  onClose: () => void;
  /** `alertdialog` para confirmação: o leitor de tela anuncia que pede decisão. */
  role?: 'dialog' | 'alertdialog';
  /** `wide` para conteúdo em colunas (a folha de respostas). */
  size?: 'default' | 'wide';
  children?: ReactNode;
}

/**
 * O diálogo das LPs — a moldura do `AuthModal` do `enamed-feedback-lp`: fundo
 * escurecido e rolagem do fundo travada enquanto ele está aberto.
 *
 * **Fecha só pelo "Fechar" (X) ou pelo Esc — clicar fora não fecha.** Os
 * diálogos daqui guardam trabalho em andamento (cadastro meio preenchido, a
 * folha de respostas do ensaio), e um toque fora do cartão — comum no celular,
 * rolando ou mirando um campo — jogava tudo fora sem aviso.
 *
 * Renderiza **no lugar**, e não num portal: as cores vêm das variáveis do
 * `LpShell`, e um portal no `body` sairia sem tema. Por isso todo `Modal`
 * precisa estar dentro de um `LpShell`.
 */
export function Modal({
  open,
  title,
  note,
  onClose,
  role = 'dialog',
  size = 'default',
  children,
}: ModalProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="lp-overlay">
      <div
        className={`lp-modal ${size === 'wide' ? 'lp-modal-wide' : ''}`}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="close"
          onClick={onClose}
          aria-label="Fechar"
        >
          {/* SVG, e não o caractere "×": o glifo segue a métrica da fonte e
              fica fora do centro do círculo. */}
          <Icon size="sm">
            <path d="M18 6 6 18M6 6l12 12" />
          </Icon>
        </button>

        <h2 className="modal-title" id={titleId}>
          {title}
        </h2>
        {/* O corpo rola por dentro: o diálogo nunca passa da altura da tela, e
            o título e o fechar ficam sempre à vista. */}
        <div className="lp-modal-body">
          {note && <p className="modal-note">{note}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
